/**
 * InfluencerAPI Service
 * Gerencia todas as requisições para a API do Portal do Influenciador
 * Baseado em: Docs/API/API_DOC_INFLUENCER_PORTAL.md
 */

const API_BASE = 'https://api.personalnutri.app';

class InfluencerAPI {
  constructor() {
    this.token = localStorage.getItem('influencer_token');
  }

  setToken(token) {
    this.token = token;
    localStorage.setItem('influencer_token', token);
  }

  clearToken() {
    this.token = null;
    localStorage.removeItem('influencer_token');
  }

  getToken() {
    return this.token || localStorage.getItem('influencer_token');
  }

  async request(endpoint, options = {}, skipAuthRedirect = false) {
    const token = this.getToken();
    
    let response;
    try {
      response = await fetch(`${API_BASE}${endpoint}`, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` }),
          ...options.headers
        }
      });
    } catch (fetchError) {
      console.error('Erro de conexão:', fetchError);
      throw new Error('Erro de conexão com o servidor. Verifique sua internet ou tente novamente.');
    }

    // Token expirado ou inválido
    if (response.status === 401) {
      // Não redirecionar automaticamente se skipAuthRedirect for true
      // Isso permite que o componente trate o erro (ex: verifyMagicLink)
      if (!skipAuthRedirect) {
        this.clearToken();
        window.location.href = '/influencer/login';
        throw new Error('Sessão expirada. Faça login novamente.');
      }
      // Se skipAuthRedirect, deixa o erro ser tratado pelo código que chamou
    }

    // Sem permissão ou conta desativada
    if (response.status === 403) {
      throw new Error('Acesso negado. Sua conta pode estar desativada.');
    }

    let data;
    try {
      const text = await response.text();
      data = text ? JSON.parse(text) : {};
    } catch (jsonError) {
      if (response.status === 500) {
        throw new Error('Problema temporário no servidor. Tente novamente ou solicite um novo link.');
      }
      console.error('Erro ao parsear resposta:', jsonError);
      throw new Error('Resposta inválida do servidor.');
    }

    // Erro 500: mensagem amigável para o fluxo de verify (backend pode estar em manutenção)
    if (response.status === 500) {
      const backendMsg = data?.message && typeof data.message === 'string' ? data.message.trim() : '';
      const friendly = 'Problema temporário no servidor. Tente novamente ou solicite um novo link.';
      const isGeneric = /interno|internal|erro 500|error 500/i.test(backendMsg);
      throw new Error(backendMsg && !isGeneric ? backendMsg : friendly);
    }

    if (!response.ok) {
      throw new Error(data.message || 'Erro na requisição');
    }

    return data;
  }

  // ==================== AUTH (Magic Link) ====================

  /**
   * Solicita envio de magic link para o email
   * @param {string} email - Email do influenciador
   */
  async requestMagicLink(email) {
    return this.request('/influencer/auth/magic-link', {
      method: 'POST',
      body: JSON.stringify({ email })
    });
  }

  /**
   * Verifica o token do magic link e retorna JWT
   * @param {string} token - Token recebido no email
   */
  async verifyMagicLink(token) {
    // skipAuthRedirect=true para permitir tratamento de erro pelo componente
    // (não redirecionar automaticamente no 401)
    const data = await this.request('/influencer/auth/verify', {
      method: 'POST',
      body: JSON.stringify({ token })
    }, true);

    if (data.status === 'success' && data.data?.token) {
      this.setToken(data.data.token);
    }

    return data;
  }

  /**
   * Logout - limpa token local
   */
  logout() {
    this.clearToken();
  }

  // ==================== PROFILE ====================

  /**
   * Obtém dados do perfil do influenciador logado
   */
  async getMe() {
    return this.request('/influencer/me');
  }

  /**
   * Atualiza dados do perfil (principalmente dados bancários/PIX)
   * @param {object} data - Dados a atualizar
   */
  async updateMe(data) {
    return this.request('/influencer/me', {
      method: 'PATCH',
      body: JSON.stringify(data)
    });
  }

  // ==================== DASHBOARD ====================

  /**
   * Obtém dashboard completo com todas as métricas
   */
  async getDashboard() {
    return this.request('/influencer/dashboard');
  }
}

// Exporta instância única (singleton)
export const influencerAPI = new InfluencerAPI();
export default influencerAPI;

